import React, { useMemo } from 'react';
import { promises, Promise, PromiseStatus } from '@/data/promises';
import { Card } from '@/components/ui/card';
import { FilterOptions } from '@/components/FilterSection';

interface PoliticianStatsProps {
  politician: string;
  imageUrl: string;
  onFilterChange: (filters: Partial<FilterOptions>) => void;
}

interface Stats {
  total: number;
  fulfilled: number;
  broken: number;
  ongoing: number;
  unknown: number;
}

const calculateStats = (politicianPromises: Promise[]): Stats => {
  return {
    total: politicianPromises.length,
    fulfilled: politicianPromises.filter(p => p.status === 'fulfilled').length,
    broken: politicianPromises.filter(p => p.status === 'broken').length,
    ongoing: politicianPromises.filter(p => p.status === 'ongoing').length,
    unknown: politicianPromises.filter(p => p.status === 'unknown').length,
  };
};

const StatBar = ({ stats }: { stats: Stats }) => {
  const total = stats.total;
  const fulfilledPercent = (stats.fulfilled / total) * 100;
  const brokenPercent = (stats.broken / total) * 100;
  const ongoingPercent = (stats.ongoing / total) * 100;
  const unknownPercent = (stats.unknown / total) * 100;

  return (
    <div className="h-2 bg-gray-200 rounded-full overflow-hidden flex">
      <div 
        className="bg-green-500 h-full transition-all duration-300" 
        style={{ width: `${fulfilledPercent}%` }}
      />
      <div 
        className="bg-yellow-500 h-full transition-all duration-300" 
        style={{ width: `${ongoingPercent}%` }}
      />
      <div 
        className="bg-red-500 h-full transition-all duration-300" 
        style={{ width: `${brokenPercent}%` }}
      />
      <div 
        className="bg-gray-400 h-full transition-all duration-300" 
        style={{ width: `${unknownPercent}%` }}
      />
    </div>
  );
};

interface StatButtonProps {
  count: number;
  label: string;
  color: string;
  hoverColor: string;
  onClick: () => void;
}

const StatButton = ({ count, label, color, hoverColor, onClick }: StatButtonProps) => (
  <button
    onClick={onClick}
    className={`text-left group transition-colors duration-200 ${count === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'} rounded px-1`}
    disabled={count === 0}
  >
    <span className={`${color} font-medium group-hover:${hoverColor}`}>{count}</span>
    <span className="text-gray-500 text-xs ml-1 group-hover:text-gray-700">{label}</span>
  </button>
);

const PoliticianCard = ({ politician, imageUrl, onFilterChange }: PoliticianStatsProps) => {
  const stats = useMemo(() => {
    const politicianPromises = promises.filter(p => p.politician === politician);
    return calculateStats(politicianPromises);
  }, [politician]);

  const handleStatusClick = (status: PromiseStatus) => {
    onFilterChange({
      politician,
      status
    });
  };

  return (
    <Card className="p-4 hover:shadow-md transition-shadow duration-200">
      <div className="flex items-center gap-4">
        <img 
          src={imageUrl} 
          alt={politician} 
          className="w-20 h-20 rounded-full object-cover border-2 border-gray-200"
        />
        <div className="flex-grow">
          <h3 className="font-semibold text-lg mb-2">{politician}</h3>
          <StatBar stats={stats} />
          <div className="mt-2 grid grid-cols-4 gap-2 text-sm">
            <StatButton
              count={stats.fulfilled}
              label="Fulfilled"
              color="text-green-600"
              hoverColor="text-green-700"
              onClick={() => handleStatusClick('fulfilled')}
            />
            <StatButton
              count={stats.ongoing}
              label="Ongoing"
              color="text-yellow-600"
              hoverColor="text-yellow-700"
              onClick={() => handleStatusClick('ongoing')}
            />
            <StatButton
              count={stats.broken}
              label="Broken"
              color="text-red-600"
              hoverColor="text-red-700"
              onClick={() => handleStatusClick('broken')}
            />
            <StatButton
              count={stats.unknown}
              label="Unknown"
              color="text-gray-600"
              hoverColor="text-gray-700"
              onClick={() => handleStatusClick('unknown')}
            />
          </div>
        </div>
      </div>
    </Card>
  );
};

interface PoliticianStatsComponentProps {
  onFilterChange: (filters: Partial<FilterOptions>) => void;
}

const PoliticianStats = ({ onFilterChange }: PoliticianStatsComponentProps) => {
  const majorPoliticians = [
    {
      name: "KP Sharma Oli",
      imageUrl: "/politicians/kp-oli.jpg"
    },
    {
      name: "Pushpa Kamal Dahal",
      imageUrl: "/politicians/prachanda.jpg"
    },
    {
      name: "Sher Bahadur Deuba",
      imageUrl: "/politicians/deuba.jpg"
    }
  ];

  return (
    <div className="mb-8">
      <h2 className="text-xl font-semibold mb-4">Major Politicians' Promise Tracker</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {majorPoliticians.map(politician => (
          <PoliticianCard 
            key={politician.name}
            politician={politician.name}
            imageUrl={politician.imageUrl}
            onFilterChange={onFilterChange}
          />
        ))}
      </div>
    </div>
  );
};

export default PoliticianStats; 